import React, {useState, useEffect, useMemo, useRef} from "react";
import Menu from "../../../../../components/layout/menu/index";
import FilterInput from "../../../../../components/filters/filterInput";
import ResetFilters from "../../../../../components/filters/resetFilters";
import ExportButton from "../../../../../components/filters/exportButton";
import UploadDocumentButton from "../../../../../components/shared/uploadDocument";
import UploadDocument from "../../../../../components/shared/modals/modalDocument/uploadDocument";
import AdminUserTable from "../../../../../components/tables/users/listTable/adminUserTable";
import { getUsers, downloadUsersExcelData, downloadExcelTemplate, uploadUsersExcel} from "../../../../../client/users";
import DownloadDocumentButton from "../../../../../components/shared/downloadDocument";
import { useNavigate } from 'react-router-dom';
import ModalAlertWarning from "../../../../../components/shared/modals/modalAlertWarning";
import ModalUserError from "../../../../../components/shared/modals/users/modalUserError";
import FilterSelect from "../../../../../components/filters/filterSelect";

export default function ListAdminUser() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); 
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const dropdownRef = useRef(null); 
  const recordsPerPage = 9;
  const [adminUsers, setAdminUsers] = useState([]);
  const [filteredAdminUsers, setFilteredAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [showModalUploadDocument, setShowModalUploadDocument] = useState(false);
  const [showModalAlertWarning, setShowModalAlertWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showModalUserError, setShowModalUserError] = useState(false); 
  const [errorExcelData, setErrorExcelData] = useState(null); 
  const [validationMessages, setValidationMessages] = useState([]); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

 const fetchAdminUsers = async (enabledFilter = null) => {
    try {
      setLoading(true);
      
      let enabledParam = null;
      if (enabledFilter === 'enabled') {
        enabledParam = true;
      } else if (enabledFilter === 'disabled') {
        enabledParam = false;
      }
      
      const response = await getUsers(1, enabledParam); // roleId = 1 para Administrador
      setAdminUsers(response.data);
    } catch (err) {
      setError(err.message || 'Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  useEffect(() => {
    if (statusFilter !== 'all') {
      fetchAdminUsers(statusFilter);
    } else {
      fetchAdminUsers();
    }
  }, [statusFilter]);
  

    useEffect(() => {
    let filtered = adminUsers;
    
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        const name = user.name || '';
        const email = user.email || '';
        const contactNumber = user.contact_number ? String(user.contact_number) : '';
        
        return (
          name.toLowerCase().includes(lowerSearchTerm) ||
          email.toLowerCase().includes(lowerSearchTerm) ||
          contactNumber.toLowerCase().includes(lowerSearchTerm)
        );
      });
    }
    
    setFilteredAdminUsers(filtered);
  }, [adminUsers, searchTerm, statusFilter]);

  const hasDataToExport = useMemo(() => {
  return filteredAdminUsers.length > 0;
}, [filteredAdminUsers]);

  const hasActiveFilters = () => {
    return searchTerm || statusFilter !== 'all';
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadLoading(true);
      const response = await downloadExcelTemplate(1); // ID 1 para ADMIN
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_administradores.xlsx');
      document.body.appendChild(link);
      link.click();
      
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar la plantilla:', error);
      alert('Error al descargar la plantilla');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleExcelUploadAdmin = async (data, file) => {
    
    try {
      const response = await uploadUsersExcel(file, 1); // Rol 1 para administradores

      if (response.data.errors && response.data.errors.length > 0) {
        setErrorMessage(response.data.errors.join(', '));
        setErrorExcelData(response.data.errorExcelBase64);
        setValidationMessages(response.data.validationMessages || []);
        setShowModalUserError(true);
        return;
      }

      if (response.data.registeredUserIds.length === 0) {
        if (response.data.errors && response.data.errors.length > 0) {
          setErrorMessage(response.data.errors.join(', '));
        } else {
          setErrorMessage('No se pudieron registrar los usuarios. Verifique los datos.');
        }
        setShowModalAlertWarning(true);
        return;
      }
          
      const adminData = data.map((item, index) => ({
        id: response.data.registeredUserIds[index], 
        fullName: item.name,
        email: item.email,
        phone: item.contact_number,
      }));
      
      
      navigate('/prev-lista-administrador', { 
        state: { 
          excelData: adminData,
          registeredUserIds: response.data.registeredUserIds
        } 
      });
      
    } catch (error) {
      console.error('Error al subir archivo:', error);
      if (error.response?.data?.errors) {
        setErrorMessage(error.response.data.errors.join(', '));
        setErrorExcelData(error.response.data.errorExcelBase64);
        setValidationMessages(error.response.data.validationMessages || []);
        setShowModalUserError(true);
      } else if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
        setShowModalAlertWarning(true);
      } else {
        setErrorMessage('Error al subir el archivo. Por favor verifique el formato.');
        setShowModalAlertWarning(true);
      }
    }
  };


  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setShowStatusFilter(false);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCurrentPage(1);
    fetchAdminUsers();
  };

const handleExport = async () => {
  try {
    setExportLoading(true);
    
    let enabledParam = null;
    if (statusFilter === 'enabled') {
      enabledParam = true;
    } else if (statusFilter === 'disabled') {
      enabledParam = false;
    }
    
    const response = await downloadUsersExcelData(1, enabledParam); // roleId = 1 para administradores
    
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'Usuarios_Administradores.xlsx';
    
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/i);
      
      if (fileNameMatch && fileNameMatch.length > 1) {
        fileName = decodeURIComponent(fileNameMatch[1]);
      } else {
        const fallbackMatch = contentDisposition.match(/filename="?([^;\r\n"]*)"?/i);
        if (fallbackMatch && fallbackMatch.length > 1) {
          fileName = fallbackMatch[1];
        }
      }
    }
    
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    
    link.remove();
    window.URL.revokeObjectURL(url);
    
  } catch (err) {
    console.error('Error al exportar los datos:', err);
    alert('Error al exportar los datos. Por favor, intente nuevamente.');
  } finally {
    setExportLoading(false);
  }
};

  const closeModalWarning = () => {
    setShowModalAlertWarning(false);
    setErrorMessage('');
  };

  const closeModalUserError = () => {
    setShowModalUserError(false);
    setErrorExcelData(null);
    setErrorMessage('');
    setValidationMessages([]);
  };

  return (
    <div className="min-h-screen">
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
      <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"}  bg-[#F7F8F9] w-full z-50 min-h-screen`}>
        <div className="md:pt-8 pt-4 pb-10 px-[3%] min-h-screen">   
            <div className="flex flex-col gap-3">
              <div className="flex flex-col items-center justify-between gap-2 lg:flex-row">
                <h1 className="text-blue font-itcbold  text-2xl md:text-[32px]">
                  Lista Usuario Administrador
                </h1>
                <div className="flex items-center justify-center gap-3">
                  <DownloadDocumentButton
                    text="Descargar Plantilla"
                    onClick={handleDownloadTemplate}
                    loading={downloadLoading}
                  />
                  <UploadDocumentButton 
                    isEnabled={true} 
                    onClick={() => setShowModalUploadDocument(true)} 
                  />
                </div>
              </div>
      
              <div className="flex flex-col justify-between gap-2 sm:flex-row">
                <FilterInput
                title="Buscar"
                placeholder="Escriba"
                gray={true}
                value={searchTerm}
                onChange={handleSearchChange}
                />

                    <div className="flex gap-2 ">
                  <div className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setShowStatusFilter(!showStatusFilter)}
                        className={`flex items-center justify-center h-[40px] px-4 p-2 border-[1px] rounded-lg ${
                          hasActiveFilters() 
                            ? 'bg-blue-light text-white border-blue-light' 
                            : 'bg-transparent text-[#1F2A37] border-[#E5E7EB] hover:bg-gray-100 '
                        }`}>
                          <p className="text-[14px] leading-none font-itcmedium">Filtrar por</p>
                      </button>

                    {showStatusFilter && (
                      <div className="absolute left-0 z-10 w-48 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg md:right-0">
                        <div className="p-4">
                          <p className="mb-2 text-sm font-itcbook text-blue">Estado:</p>
<FilterSelect
            text="Seleccionar estado"
            options={['Todos', 'Habilitado', 'Inhabilitado']}
            width="w-full"
            onSelect={(option) => {
              let statusValue = 'all';
              if (option === 'Habilitado') statusValue = 'enabled';
              if (option === 'Inhabilitado') statusValue = 'disabled';
              handleStatusFilterChange(statusValue);
            }}
            selectedOption={
              statusFilter === 'all' 
                ? 'Todos' 
                : statusFilter === 'enabled' 
                  ? 'Habilitado' 
                  : 'Inhabilitado'
            }
            bgColor="bg-white"
            border="border-[#D1D5DB]"
          />
                        </div>
                      </div>
                    )}
                  </div>
                      <ResetFilters 
                        onClick={handleResetFilters}
                        classname="hover:bg-gray-100"
                      />
                      <ExportButton
                        onClick={handleExport}
                        loading={exportLoading}
                        disabled={exportLoading || !hasDataToExport}
                      />
                  </div>
                
              </div>

              <AdminUserTable 
                recordsPerPage={recordsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                searchTerm={searchTerm}
                adminUsers={filteredAdminUsers}
                setAdminUsers={setAdminUsers}
                loading={loading}
                error={error}
              />            
  

            </div>

        </div>
      </div>
      <UploadDocument 
        showModalUploadDocument={showModalUploadDocument}
        closeModal={() => setShowModalUploadDocument(false)}
        acceptFiles=".xlsx, .xls"
        fieldMapping={{
          name: "Nombre completo (*)",
          email: "Correo electrónico (*)",
          contact_number: "Número de contacto",
        }}
        onFileProcessed={handleExcelUploadAdmin}
      />

      <ModalAlertWarning 
        showModalAlertWarning={showModalAlertWarning}
        closeModal={closeModalWarning}
        error={errorMessage}
        accept={closeModalWarning}
      />

      <ModalUserError 
        showModal={showModalUserError}
        errorMessage={errorMessage}
        excelData={errorExcelData}
        validationMessages={validationMessages}
        closeModal={closeModalUserError}
        title="Se encontraron errores en el registro"
      />

    </div>
  );
}